// import { v4 as uuidv4 } from 'uuid'; // Not needed anymore

export interface Booking {
  _id?: string;
  id?: string;
  bookingCode: string;
  customerName: string;
  phone: string;
  email: string;
  eventType: string;
  eventLocation: string;
  eventDate: string;
  eventTime: string;
  boothType: string;
  boothDisplayName?: string;
  bookingType: 'hourly' | 'perPhoto';
  hours?: number;
  totalTickets?: number;
  freeTickets?: number;
  additionalTickets?: number;
  extraTickets?: number;
  totalAmount: number;
  baseAmount: number;
  paymentStatus: 'pending' | 'completed' | 'failed';
  paymentId?: string;
  ticketDesign?: any;
  tickets?: Ticket[];
  emailSent?: boolean;
  pdfUrl?: string;
  
  // Additional Tickets (purchased after initial booking)
  additionalTicketsCount?: number;
  additionalTicketsPurchased?: Array<{
    ticketNumber: number;
    qrCode: string;
    paymentId: string;
    purchaseDate: string;
    used: boolean;
    usedAt?: string;
    usedBy?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface Ticket {
  _id?: string;
  id?: string;
  bookingId: string;
  ticketNumber: number;
  qrCode: string;
  used: boolean;
  usedAt?: string;
  type?: 'free' | 'additional';
}

class BookingService {
  private readonly API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';

  // Get authentication token
  private getAuthToken(): string | null {
    return localStorage.getItem('adminToken') || localStorage.getItem('fotomista_token');
  }

  // Create headers with authentication
  private getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // حفظ الحجز في قاعدة البيانات
  async saveBooking(bookingData: any): Promise<Booking> {
    try {
      const response = await fetch(`${this.API_URL}/api/bookings`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(bookingData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save booking');
      }

      const data = await response.json();
      console.log('✅ Booking saved to database:', data.booking);
      
      return {
        ...data.booking,
        id: data.booking._id // Ensure compatibility with frontend
      };
    } catch (error) {
      console.error('❌ Error saving booking:', error);
      throw error;
    }
  }

  // تحديث حالة الدفع
  async updatePaymentStatus(bookingId: string, status: 'completed' | 'failed', paymentId?: string): Promise<void> {
    try {
      const response = await fetch(`${this.API_URL}/api/bookings/${bookingId}/payment-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentStatus: status,
          paymentId: paymentId,
          status: status === 'completed' ? 'confirmed' : 'pending'
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update payment status');
      }

      console.log('✅ Payment status updated in database');
    } catch (error) {
      console.error('❌ Error updating payment status:', error);
      throw error;
    }
  }

  // إكمال عملية الدفع مع إرسال الإيميلات
  async completePayment(bookingId: string, paymentId?: string): Promise<any> {
    try {
      const response = await fetch(`${this.API_URL}/api/bookings/${bookingId}/complete-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentId: paymentId
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete payment');
      }

      const data = await response.json();
      console.log('✅ Payment completion processed successfully');
      return data;
    } catch (error) {
      console.error('❌ Error completing payment:', error);
      throw error;
    }
  }

  // الحصول على جميع الحجوزات من قاعدة البيانات
  async getBookings(): Promise<Booking[]> {
    try {
      const response = await fetch(`${this.API_URL}/api/bookings`, {
        headers: this.getHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch bookings');
      }

      const data = await response.json();
      
      // Ensure compatibility with frontend by adding id field
      const bookings = data.bookings.map((booking: any) => ({
        ...booking,
        id: booking._id
      }));

      console.log('✅ Loaded bookings from database:', bookings.length);
      return bookings;
    } catch (error) {
      console.error('❌ Error loading bookings:', error);
      return []; // Return empty array as fallback
    }
  }

  // الحصول على الحجوزات المؤكدة
  async getConfirmedBookings(): Promise<Booking[]> {
    try {
      const response = await fetch(`${this.API_URL}/api/bookings?paymentStatus=completed`, {
        headers: this.getHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch confirmed bookings');
      }

      const data = await response.json();
      
      const bookings = data.bookings.map((booking: any) => ({
        ...booking,
        id: booking._id
      }));

      return bookings;
    } catch (error) {
      console.error('❌ Error loading confirmed bookings:', error);
      return [];
    }
  }

  // الحصول على حجز بالـ ID
  async getBookingById(id: string): Promise<Booking | null> {
    try {
      const response = await fetch(`${this.API_URL}/api/bookings/${id}`, {
        headers: this.getHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch booking');
      }

      const data = await response.json();
      return {
        ...data.booking,
        id: data.booking._id
      };
    } catch (error) {
      console.error('❌ Error loading booking:', error);
      return null;
    }
  }

  // الحصول على التذاكر لحجز معين
  async getTicketsByBooking(bookingId: string): Promise<Ticket[]> {
    try {
      const response = await fetch(`${this.API_URL}/api/tickets/booking/${bookingId}`, {
        headers: this.getHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch tickets');
      }

      const data = await response.json();
      
      return data.tickets.map((ticket: any) => ({
        ...ticket,
        id: ticket._id
      }));
    } catch (error) {
      console.error('❌ Error loading tickets:', error);
      return [];
    }
  }

  // التحقق من صلاحية التذكرة
  async validateTicket(qrCode: string, bookingCode?: string): Promise<{ valid: boolean; ticket?: Ticket; booking?: Booking; message: string }> {
    try {
      const response = await fetch(`${this.API_URL}/api/tickets/validate`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ qrCode, bookingCode }),
        credentials: 'include'
      });

      const data = await response.json();
      
      if (!response.ok) {
        return {
          valid: false,
          message: data.message || 'فشل في التحقق من التذكرة'
        };
      }

      return {
        valid: data.valid,
        ticket: data.ticket ? { ...data.ticket, id: data.ticket._id } : undefined,
        booking: data.booking ? { ...data.booking, id: data.booking._id } : undefined,
        message: data.message || (data.valid ? 'التذكرة صالحة' : 'التذكرة غير صالحة')
      };
    } catch (error) {
      console.error('❌ Error validating ticket:', error);
      return {
        valid: false,
        message: 'خطأ في التحقق من التذكرة'
      };
    }
  }

  // استخدام التذكرة
  async useTicket(qrCode: string, bookingCode?: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.API_URL}/api/tickets/use`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({ qrCode, bookingCode }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Failed to use ticket:', errorData.message);
        return false;
      }

      const data = await response.json();
      console.log('✅ Ticket used successfully:', data);
      return true;
    } catch (error) {
      console.error('❌ Error using ticket:', error);
      return false;
    }
  }

  // تحديث حجز موجود
  async updateBooking(updatedBooking: Booking): Promise<boolean> {
    try {
      const bookingId = updatedBooking._id || updatedBooking.id;
      if (!bookingId) {
        console.error('❌ Booking ID is required for update');
        return false;
      }

      const response = await fetch(`${this.API_URL}/api/bookings/${bookingId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updatedBooking),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update booking');
      }

      console.log('✅ Booking updated in database');
      return true;
    } catch (error) {
      console.error('❌ Error updating booking:', error);
      return false;
    }
  }

  // الحصول على إحصائيات الحجوزات
  async getBookingStats(): Promise<any> {
    try {
      const response = await fetch(`${this.API_URL}/api/bookings/stats`, {
        headers: this.getHeaders(),
        credentials: 'include'
      });

      if (!response.ok) {
        console.warn('⚠️ Stats API not available, using mock data');
        // Return mock data when API is not available
        return {
          totalBookings: 12,
          confirmedBookings: 8,
          todayBookings: 2,
          totalRevenue: 5400,
          monthlyRevenue: 3200
        };
      }

      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.warn('⚠️ Stats API error, using mock data:', error);
      // Return mock data for testing
      return {
        totalBookings: 12,
        confirmedBookings: 8,
        todayBookings: 2,
        totalRevenue: 5400,
        monthlyRevenue: 3200
      };
    }
  }

  // توليد رمز الحجز (للاستخدام المحلي إذا لزم الأمر)
  generateBookingCode(): string {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `FM${year}${month}${random}`;
  }

  // Legacy methods for backward compatibility - will be removed eventually
  // These are kept to prevent breaking existing code, but they now use the API

  // توليد التذاكر - deprecated (now handled by backend automatically)
  async generateTickets(booking: Booking): Promise<Ticket[]> {
    console.warn('⚠️  generateTickets is deprecated - tickets are now generated automatically by the backend');
    return this.getTicketsByBooking(booking._id || booking.id || '');
  }

  // الحصول على جميع التذاكر - deprecated, use getTicketsByBooking instead
  async getTickets(): Promise<Ticket[]> {
    console.warn('⚠️  getTickets is deprecated - use getTicketsByBooking instead');
    return [];
  }

  // تحديث الحجز بالتذاكر - deprecated (handled by backend)
  private async updateBookingTickets(bookingId: string, tickets: Ticket[]): Promise<void> {
    console.warn('⚠️  updateBookingTickets is deprecated - handled automatically by backend');
  }
}

export const bookingService = new BookingService();